import { AbstractBlockchainService, DEFAULT_DECIMALS, NativeTokens, RewardTokens, SupportedTokens, } from '@trinity/blockchain-common';
import { IERC20Metadata__factory as IERC20MetadataFactory, MUnity__factory as MUnityFactory, MUnityTrinityEuler__factory as MUnityTrinityEulerFactory, XUnity__factory as XUnityFactory, XUnityTrinityEuler__factory as XUnityTrinityEulerFactory, } from '@trinity/contracts';
export class EvmService extends AbstractBlockchainService {
    constructor(wallet, options) {
        super();
        this.wallet = wallet;
        this.options = options;
    }
    async getContractInstances() {
        const allContractFactories = [
            this.getUnityContractInstance.bind(this),
            this.getXUnityContractInstance.bind(this),
            this.getMUnityContractInstance.bind(this),
            this.getXUnityTrinityContractInstance.bind(this),
            this.getMUnityTrinityContractInstance.bind(this),
        ];
        const allContractInstances = await Promise.all(allContractFactories.map((factory) => factory()));
        return allContractInstances;
    }
    get signer() {
        return this.wallet.getSigner();
    }
    async isWalletConnected() {
        const address = await this.wallet.getAddress();
        return Boolean(address);
    }
    async getXUnityContractInstance() {
        const signer = await this.signer;
        return XUnityFactory.connect(this.options.contractAddresses.xUnity, signer);
    }
    async getXUnityTrinityContractInstance() {
        const signer = await this.signer;
        return XUnityTrinityEulerFactory.connect(this.options.contractAddresses.xUnityTrinity, signer);
    }
    async getMUnityContractInstance() {
        const signer = await this.signer;
        return MUnityFactory.connect(this.options.contractAddresses.mUnity, signer);
    }
    async getMUnityTrinityContractInstance() {
        const signer = await this.signer;
        return MUnityTrinityEulerFactory.connect(this.options.contractAddresses.mUnityTrinity, signer);
    }
    async getUnityContractInstance() {
        const signer = await this.signer;
        return IERC20MetadataFactory.connect(this.options.contractAddresses.unity, signer);
    }
    getContractByToken(token) {
        const contractFactoriesByToken = {
            [SupportedTokens.UTY]: this.getUnityContractInstance.bind(this),
            [SupportedTokens.mUty]: this.getMUnityContractInstance.bind(this),
            [SupportedTokens.xUty]: this.getXUnityContractInstance.bind(this),
        };
        const factory = contractFactoriesByToken[token];
        return factory();
    }
    getTrinityContractByToken(token) {
        const contractFactoriesByToken = {
            [RewardTokens.xUty]: this.getXUnityTrinityContractInstance.bind(this),
            [RewardTokens.mUty]: this.getMUnityTrinityContractInstance.bind(this),
        };
        const factory = contractFactoriesByToken[token];
        return factory();
    }
    getInterestAccruingContractByToken(token) {
        const contractFactoriesByToken = {
            [RewardTokens.mUty]: this.getMUnityContractInstance.bind(this),
        };
        const factory = contractFactoriesByToken[token];
        return factory();
    }
    // Public API
    async getBalance(address, token) {
        if (token === NativeTokens.Main) {
            const balance = await this.wallet.getProvider().getBalance(address, 'latest');
            return balance || BigInt(0);
        }
        const contract = await this.getContractByToken(token);
        return this.callContract(contract, (contract) => contract.balanceOf(address));
    }
    async getTokenDecimals(token) {
        const isWalletConnected = await this.isWalletConnected();
        if (!isWalletConnected) {
            return DEFAULT_DECIMALS;
        }
        const contract = await this.getContractByToken(token);
        const decimals = await this.callContract(contract, (contract) => contract.decimals());
        return Number(decimals);
    }
    async approveTokenSpending(amount, token, rewardToken) {
        const trinityContract = await this.getTrinityContractByToken(rewardToken);
        const spenderAddress = await this.callContract(trinityContract, (contract) => contract.getAddress());
        const contract = await this.getContractByToken(token);
        await this.waitForTransactionConfirmation(contract, async (contract) => {
            return contract.approve(spenderAddress, amount);
        });
    }
    async supply(amount, rewardToken) {
        const trinityContract = await this.getTrinityContractByToken(rewardToken);
        await this.waitForTransactionConfirmation(trinityContract, async (contract) => {
            return await contract.supply(amount);
        });
    }
    async withdraw(amount, rewardToken) {
        const trinityContract = await this.getTrinityContractByToken(rewardToken);
        await this.waitForTransactionConfirmation(trinityContract, async (contract) => {
            return await contract.withdraw(amount);
        });
    }
    async getYield(address, token) {
        const contract = await this.getInterestAccruingContractByToken(token);
        return await this.callContract(contract, (contract) => contract.getInterestYield(address));
    }
    async getTokenAllowance(address, token, rewardToken) {
        const tokenContract = await this.getContractByToken(token);
        const trinityContract = await this.getTrinityContractByToken(rewardToken);
        const trinityContractAddress = await this.callContract(trinityContract, (contract) => contract.getAddress());
        return await this.callContract(tokenContract, (tokenContract) => {
            return tokenContract.allowance(address, trinityContractAddress);
        });
    }
}
//# sourceMappingURL=evm-service.js.map